import React, { useEffect, useState } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { MarketplaceHeader } from '../components/layout/MarketplaceHeader';
import { Footer } from '../components/layout/Footer';
import { CartItem } from '../components/restaurant/CartItem';
import { CheckoutUpsellModal } from '../components/restaurant/CheckoutUpsellModal';
import { EmiliusCheckoutUpsellModal } from '../components/restaurant/EmiliusCheckoutUpsellModal';
import { useCart } from '../lib/cart';
import { formatCurrency } from '../lib/utils';
import { Button } from '../components/ui/Button';
import { EmptyState } from '../components/ui/EmptyState';
import { useKitchenPause } from '../lib/kitchen-pause-context';
import { useLanguage } from '../lib/i18n';
import { useTableOrder } from '../lib/table-context';
import { useRestaurantScheduleStatus } from '../lib/schedule';
import { ShoppingBag, ArrowLeft, Store } from 'lucide-react';
export function CartPage() {
  const navigate = useNavigate();
  const {
    cart,
    updateQuantity,
    removeItem,
    getTotal,
    clearCart,
    getDiscount
  } = useCart();
  const {
    isKitchenPaused,
    monitorRestaurant
  } = useKitchenPause();
  const {
    t
  } = useLanguage();
  const {
    tableOrder,
    isTableOrder
  } = useTableOrder();
  // Schedule-based open/closed enforcement
  const scheduleStatus = useRestaurantScheduleStatus(cart.restaurantSlug);
  const isClosedBySchedule = scheduleStatus.isClosedForOrdering;
  const isNatucafeQrTable = isTableOrder && !!tableOrder && String(tableOrder.restaurantId) === '19';
  // ─── Crack Pollo (id 20) upsell modal ────────────────────────────────
  const isCrackPolloCart = String(cart.restaurantId) === '20';
  // ─── Emilius Pizza (id 27) upsell modal ──────────────────────────────
  const isEmiliusCart = String(cart.restaurantId) === '27';
  const [showUpsellModal, setShowUpsellModal] = useState(false);
  const [showEmiliusUpsellModal, setShowEmiliusUpsellModal] = useState(false);
  const [upsellAlreadyShown, setUpsellAlreadyShown] = useState(false);
  const handleCheckoutClick = () => {
    if (isClosedBySchedule) {
      alert(`${cart.restaurantName || 'El restaurante'} está cerrado en este momento. ${scheduleStatus.nextOpeningHint || 'Vuelve durante nuestro horario de atención.'}`);
      return;
    }
    if (cart.items.length > 0 && !upsellAlreadyShown) {
      if (isCrackPolloCart) {
        setShowUpsellModal(true);
        return;
      }
      if (isEmiliusCart) {
        setShowEmiliusUpsellModal(true);
        return;
      }
    }
    navigate('/checkout');
  };
  const handleUpsellContinue = () => {
    setShowUpsellModal(false);
    setShowEmiliusUpsellModal(false);
    setUpsellAlreadyShown(true);
    navigate('/checkout');
  };
  // Register restaurant with central kitchen pause monitor
  useEffect(() => {
    if (cart.restaurantId) {
      console.log('[NOIZA][CartPage] Registering restaurant:', cart.restaurantId);
      monitorRestaurant(cart.restaurantId);
    }
  }, [cart.restaurantId, monitorRestaurant]);
  const subtotal = getTotal();
  const discount = getDiscount();
  // In a real app, delivery fee would be calculated based on distance/settings
  const deliveryFeeEstimate = cart.items.length > 0 ? 1500 : 0;
  const total = subtotal + deliveryFeeEstimate;
  if (cart.items.length === 0) {
    return <div className="min-h-screen w-full flex flex-col bg-brand-offwhite" data-id="element-5248">
        <MarketplaceHeader data-id="element-5249" />
        <main className="flex-1 flex items-center justify-center py-12" data-id="element-5250">
          <EmptyState icon={ShoppingBag} title={t('cart_empty')} description={t('cart_empty_desc')} actionLabel={t('explore')} onAction={() => navigate('/restaurants')} data-id="element-5251" />
        </main>
        <Footer data-id="element-5252" />
      </div>;
  }
  return <div className="min-h-screen w-full flex flex-col bg-brand-offwhite" data-id="element-5253">
      <MarketplaceHeader data-id="element-5254" />

      <main className="flex-1 max-w-3xl mx-auto w-full px-4 sm:px-6 py-8" data-id="element-5255">
        <div className="flex items-center justify-between mb-8" data-id="element-5256">
          <div className="flex items-center gap-4" data-id="element-5257">
            <button onClick={() => navigate(-1)} className="p-2 -ml-2 text-gray-400 hover:text-gray-900 rounded-full hover:bg-gray-50 transition-colors" data-id="element-5258">
              <ArrowLeft className="w-6 h-6" data-id="element-5259" />
            </button>
            <h1 className="text-3xl font-bold text-gray-900" data-id="element-5260">
              {t('cart_title')}
            </h1>
          </div>
          <button onClick={clearCart} className="text-sm font-medium text-red-500 hover:text-red-600 transition-colors" data-id="element-5261">
            {t('cart_clear')}
          </button>
        </div>

        <div className="bg-white rounded-2xl border border-gray-100 shadow-sm overflow-hidden mb-8" data-id="element-5262">
          <div className="bg-gray-50 px-6 py-4 border-b border-gray-100 flex items-center gap-3" data-id="element-5263">
            <Store className="w-5 h-5 text-gray-500" data-id="element-5264" />
            <span className="font-semibold text-gray-900" data-id="element-5265">
              {cart.restaurantName}
            </span>
          </div>

          <div className="px-6 py-2" data-id="element-5266">
            {cart.items.map(item => <CartItem key={item.id} item={item} onUpdateQuantity={updateQuantity} onRemove={removeItem} data-id="element-5267" />)}
          </div>
        </div>

        <div className="bg-white rounded-2xl border border-gray-100 shadow-sm p-6 mb-8" data-id="element-5268">
          <div className="space-y-4" data-id="element-5269">
            <div className="flex justify-between text-gray-600" data-id="element-5270">
              <span data-id="element-5271">{t('subtotal')}</span>
              <span data-id="element-5272">{formatCurrency(subtotal + (discount?.amount ?? 0))}</span>
            </div>
            {discount && <div className="flex justify-between text-emerald-600 font-medium" data-id="element-5273">
                <span data-id="element-5274">{discount.label}</span>
                <span data-id="element-5275">-{formatCurrency(discount.amount)}</span>
              </div>}
            <div className="flex justify-between text-gray-600" data-id="element-5276">
              <span data-id="element-5277">{t('delivery_fee')}</span>
              <span data-id="element-5278">{formatCurrency(deliveryFeeEstimate)}</span>
            </div>
            <div className="pt-4 border-t border-gray-100 flex justify-between text-lg font-bold text-gray-900" data-id="element-5279">
              <span data-id="element-5280">{t('total')}</span>
              <span data-id="element-5281">{formatCurrency(total)}</span>
            </div>
          </div>
        </div>

        <Button onClick={handleCheckoutClick} className="w-full h-14 text-lg" disabled={isKitchenPaused || isClosedBySchedule} data-id="element-5282">
          {isClosedBySchedule ? `🔒 Cerrado${scheduleStatus.nextOpeningHint ? ` — ${scheduleStatus.nextOpeningHint}` : ''}` : isKitchenPaused ? t('kitchen_paused') : isNatucafeQrTable ? 'Terminar Pedido' : t('cart_checkout')}
        </Button>
      </main>

      {isCrackPolloCart && cart.restaurantId && cart.restaurantName && <CheckoutUpsellModal isOpen={showUpsellModal} onClose={() => setShowUpsellModal(false)} onContinue={handleUpsellContinue} restaurantId={cart.restaurantId} restaurantName={cart.restaurantName} restaurantSlug={cart.restaurantSlug ?? undefined} data-id="element-5283" />}

      {isEmiliusCart && cart.restaurantId && cart.restaurantName && <EmiliusCheckoutUpsellModal isOpen={showEmiliusUpsellModal} onClose={() => setShowEmiliusUpsellModal(false)} onContinue={handleUpsellContinue} restaurantId={cart.restaurantId} restaurantName={cart.restaurantName} restaurantSlug={cart.restaurantSlug ?? undefined} data-id="element-5284" />}

      <Footer data-id="element-5285" />
    </div>;
}